import React from "react";
import {
  IoCheckmarkCircle,
  IoCloseCircle,
  IoRemoveCircle,
  IoRefresh,
} from "react-icons/io5";
import { useOfficePortalAccessQuery } from "../../apis/office";

// Every verdict here comes from calling REA and Domain, not from anything we
// have stored — an office that has never listed has no stored portal state,
// which is exactly when this panel is worth reading.

// The four capabilities we track, same four on both portals.
const ROWS = [
  ["export", "Export listings"],
  ["enquiries", "Buyer enquiries"],
  ["push", "Portal push"],
  ["listingPerformance", "Listing performance"],
];

const Verdict = ({ check }) => {
  if (!check) return null;
  const icon =
    check.ok === true ? (
      <IoCheckmarkCircle className="text-green-600 flex-shrink-0 text-base" />
    ) : check.ok === false ? (
      <IoCloseCircle className="text-red-600 flex-shrink-0 text-base" />
    ) : (
      <IoRemoveCircle className="text-darkgray flex-shrink-0 text-base" />
    );
  return <span className="mt-0.5">{icon}</span>;
};

const Row = ({ label, check }) => (
  <div className="flex items-start gap-2">
    <Verdict check={check} />
    <div className="flex-1">
      <div className="text-black">{label}</div>
      {check?.detail && (
        <div className="text-xs text-darkgray">{check.detail}</div>
      )}
    </div>
  </div>
);

export default function OfficePortalAccess({ office }) {
  const { data, isFetching, isError, error, refetch, isFetched } =
    useOfficePortalAccessQuery(office?._id);

  if (!office) return null;

  const reaForeign = data?.rea?.foreignLiveAds ?? [];
  const domainForeign = data?.domain?.foreignLiveAds ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium text-black">Portal Access</div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-mediumgray text-black hover:bg-lightgray disabled:opacity-50"
        >
          <IoRefresh className={isFetching ? "animate-spin" : ""} />
          {isFetching ? "Checking…" : isFetched ? "Refresh" : "Check now"}
        </button>
      </div>

      <div className="bg-lightgray rounded-lg p-4 space-y-4">
        {!isFetched && !isFetching && (
          <p className="text-xs text-darkgray">
            Calls REA and Domain live to see what this office can actually do.
          </p>
        )}

        {isError && (
          <p className="text-xs text-red-600">
            {error?.response?.data?.message || "Portal check failed."}
          </p>
        )}

        {data && (
          <>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-darkgray">realestate.com.au</span>
                <span className="text-black font-medium">
                  {data.reaId || "no agent id"}
                </span>
              </div>
              <div className="space-y-2">
                {ROWS.map(([key, label]) => (
                  <Row key={key} label={label} check={data.rea?.[key]} />
                ))}
              </div>
            </div>

            <div className="border-t border-mediumgray pt-4">
              <div className="flex justify-between mb-2">
                <span className="text-darkgray">Domain</span>
                <span className="text-black font-medium">
                  {data.domainId || "no agency id"}
                </span>
              </div>
              <div className="space-y-2">
                {ROWS.map(([key, label]) => (
                  <Row key={key} label={label} check={data.domain?.[key]} />
                ))}
              </div>
            </div>

            {(reaForeign.length > 0 || domainForeign.length > 0) && (
              <div className="border-t border-mediumgray pt-4">
                <div className="text-black mb-1">
                  Live ads fed by another provider
                </div>
                <p className="text-xs text-darkgray mb-2">
                  Publishing one of these properties without reusing the id
                  shown creates a second ad. REA suspends a feed after three
                  duplicates.
                </p>

                {reaForeign.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs text-darkgray mb-1">
                      realestate.com.au — {reaForeign.length}
                    </div>
                    <div className="space-y-1">
                      {reaForeign.map((ad) => (
                        <div
                          key={ad.adId}
                          className="text-xs flex gap-2 justify-between"
                        >
                          <span className="text-black">
                            {ad.address || ad.adId}
                          </span>
                          <span className="text-darkgray">{ad.uniqueId}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {domainForeign.length > 0 && (
                  <div>
                    <div className="text-xs text-darkgray mb-1">
                      Domain — {domainForeign.length}
                    </div>
                    <div className="space-y-1">
                      {domainForeign.map((ad) => (
                        <div
                          key={ad.adId}
                          className="text-xs flex gap-2 justify-between"
                        >
                          <span className="text-black">
                            {ad.address || ad.adId}
                          </span>
                          <span className="text-darkgray">
                            {ad.providerSystem} · {ad.providerAdId}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-darkgray">
              Checked {new Date(data.checkedAt).toLocaleString("en-AU")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
